import React, { useMemo } from "react"
import moment from "moment-timezone"
import { CheckCircle2, Clock, XCircle, Info, ArrowRight } from "lucide-react"

import styles from "./payment.module.scss"

const formatGbp = (value) =>
	Number(parseFloat((value || "0").toString()) || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const base64UrlToString = (segment) => {
	try {
		const base64 = segment.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((segment.length + 3) % 4)
		return decodeURIComponent(Array.prototype.map.call(atob(base64), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join(""))
	} catch (err) {
		return ""
	}
}

/**
 * Payment result page — reskinned. Reads the redirect params Stripe (or the
 * legacy AMEX flow) appends and shows a success / processing / failed status.
 * Purely presentational; the payment record is written by the Stripe webhook.
 */
export default function Payment() {
	const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])

	const stripeIntentId = searchParams.get("payment_intent") || ""
	const stripeStatus = (searchParams.get("redirect_status") || "").toLowerCase()
	const isStripeReturn = Boolean(stripeIntentId)

	const statusParam = (searchParams.get("status") || "").toLowerCase()
	const transactionDetailsRaw = searchParams.get("transaction_details") || ""

	// Decode the legacy AMEX JWS payload for amount/reference details.
	const decoded = useMemo(() => {
		if (!transactionDetailsRaw || transactionDetailsRaw.split(".").length < 2) return { payload: null }
		const [, payloadSeg] = transactionDetailsRaw.split(".")
		try {
			const payloadJson = base64UrlToString(payloadSeg)
			return { payload: JSON.parse(payloadJson) }
		} catch (_) {
			return { payload: null }
		}
	}, [transactionDetailsRaw])

	const paymentStatus = (decoded.payload?.payment_status || "").toString()
	const amountRaw = decoded.payload?.amount
	const currency = decoded.payload?.currency || "GBP"
	const bankReferenceId = decoded.payload?.bank_reference_id || ""
	const orderId = decoded.payload?.order_id || ""
	const timestampMs = typeof decoded.payload?.timestamp === "number" ? decoded.payload.timestamp : undefined

	const { heading, subheading, tone } = useMemo(() => {
		if (isStripeReturn) {
			if (stripeStatus === "succeeded") return { tone: "success", heading: "Payment successful", subheading: "Thank you — your payment is complete and will appear on your booking shortly." }
			if (stripeStatus === "processing") return { tone: "processing", heading: "Payment processing", subheading: "Your bank has accepted the payment and it's being settled now. This will show on your booking shortly." }
			if (stripeStatus === "failed") return { tone: "failed", heading: "Payment failed", subheading: "Unfortunately your payment couldn't be completed. Please try again or use another method." }
			return { tone: "neutral", heading: "Payment received", subheading: "We've received your payment. Please allow a short time for it to appear on your booking." }
		}

		if (statusParam === "success") {
			if (paymentStatus === "AcceptedSettlementCompleted") return { tone: "success", heading: "Payment successful", subheading: "Thank you — your payment has completed successfully. A receipt will appear on your booking shortly." }
			if (paymentStatus === "AcceptedSettlementInProcess") return { tone: "processing", heading: "Payment processing", subheading: "Your bank has accepted the payment and settlement is in process. This will show on your booking shortly." }
			return { tone: "success", heading: "Payment complete", subheading: "Your payment has been completed." }
		}
		if (statusParam === "cancelled") return { tone: "failed", heading: "Payment cancelled", subheading: "You cancelled before completion. No funds have been taken." }
		if (["failure", "failed", "error"].includes(statusParam)) return { tone: "failed", heading: "Payment failed", subheading: "Unfortunately your payment couldn't be completed. Please try again or use another method." }

		return { tone: "neutral", heading: "Payment status", subheading: "We've received a response from the payment provider." }
	}, [isStripeReturn, stripeStatus, statusParam, paymentStatus])

	const ToneIcon = tone === "success" ? CheckCircle2 : tone === "processing" ? Clock : tone === "failed" ? XCircle : Info

	const formattedAmount = formatGbp(amountRaw)
	const hasDetails = (amountRaw && Number(amountRaw) > 0) || bankReferenceId || timestampMs || orderId

	return (
		<div className={styles.result}>
			<div className={`${styles.icon} ${styles[tone]}`}>
				<ToneIcon size={34} />
			</div>

			<h1>{heading}</h1>
			<p className={styles.sub}>{subheading}</p>

			{hasDetails ? (
				<dl className={styles.details}>
					{amountRaw && Number(amountRaw) > 0 ? (
						<div>
							<dt>Amount</dt>
							<dd>
								{currency === "GBP" ? "£" : ""}
								{formattedAmount} {currency !== "GBP" ? currency : ""}
							</dd>
						</div>
					) : null}
					{bankReferenceId ? (
						<div>
							<dt>Bank reference</dt>
							<dd>{bankReferenceId}</dd>
						</div>
					) : null}
					{timestampMs ? (
						<div>
							<dt>Date / time</dt>
							<dd>{moment(timestampMs).format("DD MMM YYYY, HH:mm")}</dd>
						</div>
					) : null}
					{orderId ? (
						<div>
							<dt>Booking ref</dt>
							<dd>{orderId.split("-")[0]}</dd>
						</div>
					) : null}
				</dl>
			) : null}

			<button
				type="button"
				className={styles.button}
				onClick={() => (window.location.href = window.location.pathname.replace(/\/payment$/, "/payments"))}>
				View payments
				<ArrowRight size={17} />
			</button>

			<p className={styles.note}>If your payment doesn't appear immediately, please allow up to 24 hours for it to be assigned to your booking.</p>
		</div>
	)
}
